import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useResource, formatDate, daysUntil } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { ProviderOrgChart } from "../components/ProviderOrgChart";
import type { Participant, Shift } from "../lib/types";

/**
 * Provider Portal preview, prototype-parity: what a partner provider sees in
 * their own portal login, viewed from the admin side. Pick a provider and see
 * exactly their participants — never other providers', never pay rates, never
 * TrustCare's internal notes. Real partner logins get the actual portal
 * (/partner); this page lets an admin sanity-check a provider's view without
 * borrowing their account.
 */

interface CompanyRow { id: string; name: string; }

const local = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const weekRange = () => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: `${local(monday)}T00:00:00Z`, to: `${local(sunday)}T23:59:59Z` };
};

export const ProviderPortalPreview = () => {
    const companies = useResource<CompanyRow[]>("/companies");
    const participants = useResource<Participant[]>("/participants");
    const { from, to } = weekRange();
    const shifts = useResource<Shift[]>(`/shifts?from=${from}&to=${to}`);

    const [providerId, setProviderId] = useState("");
    const provider = providerId || companies.data?.[0]?.id || "";

    const theirs = useMemo(
        () => (participants.data ?? []).filter((p) => p.company_id === provider),
        [participants.data, provider]);

    const hoursFor = (id: string) =>
        (shifts.data ?? [])
            .filter((s) => s.participant_id === id && s.status !== "cancelled")
            .reduce((a, s) => a + Math.max(0, new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000, 0);

    const isIncomplete = (p: Participant) => !p.ndis_number || !p.plan_start_date || !p.plan_end_date;
    const totalHours = theirs.reduce((a, p) => a + hoursFor(p.id), 0);
    const incomplete = theirs.filter(isIncomplete).length;

    return (
        <div>
            <div className="card" style={{ marginBottom: "0.8rem" }}>
                <div className="card-body" style={{ display: "flex", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
                    <b style={{ fontSize: "0.75rem", letterSpacing: "1px", color: "var(--muted)" }}>VIEWING AS</b>
                    <select value={provider} onChange={(e) => setProviderId(e.target.value)} style={{ maxWidth: 280 }}>
                        {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <span className="muted" style={{ fontSize: "0.8rem" }}>
                        Providers see only their own participants — never other providers', never pay rates, never TrustCare's internal notes.
                    </span>
                </div>
            </div>

            <div className="stat-strip">
                <div className="stat good"><div className="num">{theirs.length}</div><div className="lbl">Their participants</div></div>
                <div className={`stat ${incomplete ? "warn" : "good"}`}><div className="num">{incomplete}</div><div className="lbl">Records needing completion</div></div>
                <div className="stat good"><div className="num">{totalHours.toFixed(0)}</div><div className="lbl">TrustCare hours this week</div></div>
            </div>

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr><th>Participant</th><th>NDIS plan</th><th>Record status</th><th>TrustCare hours this week</th><th /></tr>
                            </thead>
                            <tbody>
                                {theirs.map((p) => {
                                    const days = p.plan_end_date ? daysUntil(p.plan_end_date) : null;
                                    return (
                                        <tr key={p.id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                    <InitialsAvatar name={p.full_name} size={28} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{p.full_name}</div>
                                                        <div className="mono" style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                                            {p.ndis_number ? `NDIS ${p.ndis_number}` : "NDIS number missing"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {days == null ? <span className="chip mute">No plan on file</span>
                                                    : days < 0 ? <span className="chip block">Plan expired</span>
                                                    : days < 60 ? <span className="chip warn">{days} days left</span>
                                                    : <span className="chip pass">{days} days left</span>}
                                                {p.plan_end_date && (
                                                    <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>to {formatDate(p.plan_end_date)}</div>
                                                )}
                                            </td>
                                            <td>
                                                {isIncomplete(p)
                                                    ? <span className="chip warn">Info requested</span>
                                                    : <span className="chip pass">Complete</span>}
                                            </td>
                                            <td className="mono">{hoursFor(p.id).toFixed(1)} h</td>
                                            <td><Link className="icon-btn" to={`/participants?id=${p.id}`}>Open record</Link></td>
                                        </tr>
                                    );
                                })}
                                {!theirs.length && (
                                    <tr><td colSpan={5} className="empty">This provider has no participants linked yet — link one from the Participants page.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {provider && (
                <div className="card" style={{ marginTop: "0.8rem" }}>
                    <div className="card-head"><h2>Their org chart</h2></div>
                    <div className="card-body">
                        <p className="muted" style={{ marginBottom: "0.5rem" }}>
                            Built from how this provider registers their people — set each person's
                            position (job title) on their contact record, and the reporting line below.
                        </p>
                        <ProviderOrgChart companyId={provider} />
                    </div>
                </div>
            )}

            <div className="card" style={{ marginTop: "0.8rem" }}>
                <div className="card-body">
                    <b style={{ fontSize: "0.9rem" }}>How registration works</b>
                    <p className="muted" style={{ fontSize: "0.83rem", lineHeight: 1.7, marginTop: 6 }}>
                        1 — The provider registers a participant in their portal (or TrustCare adds them from the CRM — either side can start the record).<br />
                        2 — The record reaches TrustCare and the provider can keep completing it meanwhile.<br />
                        3 — TrustCare reviews, fills anything missing, links services and approves — only then can shifts be rostered.<br />
                        4 — After approval both sides keep maintaining their own parts: the provider owns care plans and contacts; TrustCare owns rostering, notes and billing.
                    </p>
                </div>
            </div>
        </div>
    );
};
